import CloseIcon from "@mui/icons-material/Close"
import SearchIcon from "@mui/icons-material/Search"
import { useApi } from "auth/useApi3"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"

import {
  Box,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  List,
  ListItem,
  TextField,
  Typography
} from "@mui/material"
import { styled } from "@mui/material/styles"

const StyledGrid = styled(Grid)({
  display: "flex",
  width: "100%"
})

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiInputBase-root": {
    paddingRight: "2px",
    paddingLeft: "8px"
  }
}))

const PeoplePickerSearch = ({ onSearch, setSearchString, searchString, setValue, errors, touched }) => {
  const key_up = (e) => {
    var enterKey = 13
    if (e.which === enterKey) {
      onSearch(e.target.value)
    }
  }

  const { t } = useTranslation("translation", { keyPrefix: "learning" })

  return (
    <Grid container spacing={2}>
      <StyledGrid item xs={12} flexDirection="column">
        <Box mb={1}>
          <InputLabel>
            <Typography fontWeight={600} color="textPrimary" variant="labelMedium">
              {t(`admin.participants.search_for_users`, "Search for users")}
            </Typography>
          </InputLabel>
        </Box>
        <FormControl>
          <StyledTextField
            variant="outlined"
            value={searchString}
            onChange={(e) => {
              setSearchString(e.target.value)
            }}
            onKeyUp={(e) => key_up(e)}
            InputProps={{
              startAdornment: (
                <IconButton
                  type="button"
                  aria-label="search"
                  onClick={() => {
                    onSearch(searchString)
                  }}
                  size="small">
                  <SearchIcon />
                </IconButton>
              ),
              endAdornment: (
                <IconButton
                  sx={{ padding: "8px" }}
                  type="button"
                  aria-label="search"
                  onClick={() => {
                    setSearchString("")
                    setValue("")
                  }}
                  size="small">
                  <CloseIcon />
                </IconButton>
              )
            }}
          />
        </FormControl>

        <Typography mt={1} color="error" variant="bodySmall">
          {touched && errors ? errors : ""}
        </Typography>
      </StyledGrid>
    </Grid>
  )
}

const StyledList = styled(List)(({ theme }) => ({
  boxShadow: "rgba(149, 157, 165, 0.1) 0px 8px 24px;",
  borderRadius: "10px"
}))

const StyledListItem = styled(ListItem)(({ theme }) => ({
  cursor: "pointer",
  transition: "background  0.3s ease",
  "&:hover": {
    backgroundColor: theme.palette.grey[100]
  }
}))

const PeoplePickerResultList = ({ searchResult, onSelect, value, formRef, setSearchString }) => {
  const { t } = useTranslation("translation", { keyPrefix: "learning" })
  return (
    value && (
      <StyledList>
        {searchResult.status === 1 ? (
          <ListItem>
            <Typography variant="paragraphMedium">
              {t(`admin.participants.searching`, "Searching...")}
            </Typography>
          </ListItem>
        ) : null}
        {searchResult.status === 2 && searchResult.data.length === 0 && value ? (
          <ListItem>
            <Typography variant="paragraphMedium">
              {t(`admin.participants.no_participants_found`, "No people found")}
            </Typography>
          </ListItem>
        ) : null}

        {searchResult.status === 2 && value
          ? searchResult.data.map((item, key) => {
              // det är ett paged result
              return (
                <StyledListItem
                  key={key}
                  onClick={(event) => {
                    onSelect(item, event)
                    formRef?.current.setFieldValue("userId", item.id)
                    setSearchString(item.name)
                  }}>
                  <div>
                    <Typography variant="bodySmall">{item.name}</Typography>
                    <Typography variant="caption">{item.email}</Typography>
                  </div>
                </StyledListItem>
              )
            })
          : null}
      </StyledList>
    )
  )
}

const PeoplePicker = ({
  errors,
  touched,
  onSelect,
  searchUrl,
  children,
  clickedItem,
  selectedValue,
  setSelectedValue,
  formRef
}) => {
  // onSearch, define api here
  // onlistClick, and bubble up the click on list item
  const [searchResult, searchApi] = useApi()
  const [value, setValue] = useState(null)
  const [searchString, setSearchString] = useState("")
  const onSearch = (value) => {
    setSelectedValue("")
    // searches and passes result to list when done
    setValue(value)
    if (value) {
      searchApi.get(searchUrl.concat("?search=").concat(value))
    }
  }

  return (
    <Grid container>
      <Grid item xs={12}>
        <PeoplePickerSearch
          errors={errors}
          touched={touched}
          clickedItem={clickedItem}
          setValue={setValue}
          searchString={searchString}
          setSearchString={setSearchString}
          onSearch={onSearch}
          formRef={formRef}
        />
      </Grid>
      <Grid item xs={12}>
        {!selectedValue && (
          <PeoplePickerResultList
            formRef={formRef}
            setSearchString={setSearchString}
            clickedItem={clickedItem}
            searchString={searchString}
            searchResult={searchResult}
            onSelect={onSelect}
            value={value}
          />
        )}
      </Grid>
      {children}
    </Grid>
  )
}

const PeoplePickerContainer = ({
  errors,
  touched,
  inititalSelectedValue,
  values,
  onChange,
  searchUrl,
  children,
  clickedItem,
  showUserError,
  formRef
}) => {
  // displays the result or lets you change it and bubble up the selection to whatever formik
  const [selectedValue, setSelectedValue] = React.useState(inititalSelectedValue)

  return (
    <>
      <PeoplePicker
        errors={errors}
        touched={touched}
        values={values}
        formRef={formRef}
        showUserError={showUserError}
        clickedItem={clickedItem}
        children={children}
        searchUrl={searchUrl}
        setSelectedValue={setSelectedValue}
        selectedValue={selectedValue}
        onSelect={(item) => {
          setSelectedValue(item)
          onChange(item)
        }}
      />
    </>
  )
}

export default PeoplePickerContainer
